import {
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Youtube,
} from 'lucide-react';
import { GetPagesQuery } from '~/generated/graphql';

const Footer = ({ pages }: { pages: GetPagesQuery['pages']['items'] }) => {
  return (
    <footer className="bg-white text-gray-800 py-10 sm:py-16 border-t border-gray-100 font-['Poppins']">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">

          {/* Company Section */}
          <div className="space-y-6">
            <div>
              <h6
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 600,
                  fontSize: '28px',
                  lineHeight: '36px',
                  letterSpacing: '-0.04em',
                  color: '#1A090E',
                }}
                className="mb-1"
              >
                Company
              </h6>
              <div>
                <p
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 400,
                    fontSize: '18px',
                    lineHeight: '24px',
                    letterSpacing: '-0.02em',
                    color: '#54454A',
                    marginBottom: '4px',
                  }}
                >
                  Redtab Private Limited
                </p>
                <p
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '-0.01em',
                    color: '#9A9798',
                    marginBottom: '0px',
                  }}
                >
                  CIN: U74900DL2015PTC286208
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#FFE8EC] flex items-center justify-center text-[#EC0045]">
                  <MapPin size={20} />
                </div>
                <div className="text-sm text-gray-600 leading-relaxed mt-0.5">
                  <p
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: '-0.01em',
                      color: '#54454A',
                    }}
                  >
                    Ground Floor, 12A, 94 KTM,
                  </p>
                  <p
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: '-0.01em',
                      color: '#54454A',
                    }}
                  >
                    Lalitpur, Patan Dhoka - 110001
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#FFE8EC] flex items-center justify-center text-[#EC0045]">
                  <Phone size={20} />
                </div>
                <span
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '-0.01em',
                    color: '#54454A',
                  }}
                >
                  +91-11-41717171
                </span>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#FFE8EC] flex items-center justify-center text-[#EC0045]">
                  <Mail size={20} />
                </div>
                <a
                  href="mailto:help@redtab.com"
                  className="hover:text-[#EC0045] transition-colors"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '-0.01em',
                    color: '#54454A',
                  }}
                >
                  help@redtab.com
                </a>
              </div>
            </div>
          </div>

          {/* Know More Section */}
          <div className="md:pl-12">
            <h6
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                fontSize: '28px',
                lineHeight: '36px',
                letterSpacing: '-0.04em',
                color: '#1A090E',
              }}
              className="mb-6"
            >
              Know More
            </h6>
            <div className="flex flex-row gap-8 text-sm">
              {/* Static links as per design image 'About', 'Privacy', keeping dynamic fallback */}
              {pages.length > 0 ? (
                pages.map((page) => (
                  <a
                    key={page.id}
                    href={`/pages/${page.slug}`}
                    className="hover:text-[#EC0045] transition-colors"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: '-0.01em',
                      color: '#54454A',
                    }}
                  >
                    {page.title}
                  </a>
                ))
              ) : (
                <>
                  <a
                    href="/about"
                    className="hover:text-[#EC0045] transition-colors"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: '-0.01em',
                      color: '#54454A',
                    }}
                  >
                    About
                  </a>
                  <a
                    href="/privacy"
                    className="hover:text-[#EC0045] transition-colors"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: '-0.01em',
                      color: '#54454A',
                    }}
                  >
                    Privacy
                  </a>
                </>
              )}
            </div>
          </div>

          {/* Connect with us Section */}
          <div>
            <h6
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                fontSize: '28px',
                lineHeight: '36px',
                letterSpacing: '-0.04em',
                color: '#1A090E',
              }}
              className="mb-6"
            >
              Connect with us
            </h6>
            <div className="space-y-4">
              <p
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  letterSpacing: '-0.01em',
                  color: '#54454A',
                }}
              >
                Follow us on social media
              </p>
              <div className="flex gap-4">
                {[
                  { icon: Linkedin, href: 'https://www.linkedin.com/company/redtab' },
                  { icon: Instagram, href: 'https://www.instagram.com/redtab' },
                  { icon: Youtube, href: 'https://www.youtube.com/redtab' },
                ].map((social, idx) => (
                  <a
                    key={idx}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-xl bg-[#FFE8EC] flex items-center justify-center text-[#EC0045] hover:bg-[#EC0045] hover:text-white transition-all duration-300"
                  >
                    <social.icon size={20} />
                  </a>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </footer>
  );
};

export default Footer;
